import { useEffect, useState } from 'react';
import { IconLayer } from '@deck.gl/layers/typed';
import { useLazyGetVesselRouteQuery } from 'app/api/target';

import { useAppDispatch, useAppSelector } from 'app/store/store';
import { getOptimalRoute, svgToDataURL } from 'app/helpers';
import { getRouteArrowIcon } from 'helpers/getWebGLPointIcon';
import { ViewportData } from 'ais-facade-api/api';
import { setHoveredRoutePoint, setRouteForSelectedVessel } from 'app/utils/selectedTargetsSlice';
import { ArcLayer } from 'deck.gl/typed';
import { ColorizedRouteFragment, getColorizedRoute } from 'app/helpers/getColorizedRoute';

export const useLayerRoute = (isHistoryMode?: boolean) => {
  const mode = useAppSelector((state) => state.mapStateSlice.mode);
  const isNotBaseMode = mode === 'analytics' || mode === 'playback';

  const dispatch = useAppDispatch();
  const selectedTarget = useAppSelector((state) => state.selectedTargetsSlice?.selectedTarget);
  const route = useAppSelector((state) => state.selectedTargetsSlice?.route);

  const targetObject = selectedTarget?.target?.object;
  const [getRoute, { ...routeState }] = useLazyGetVesselRouteQuery();
  const isLoading = routeState.isFetching || routeState.isLoading;
  const [colorizedRoute, setColorizedRoute] = useState<ColorizedRouteFragment[] | null>(null);

  useEffect(() => {
    if (!isHistoryMode && selectedTarget && selectedTarget.type === 'vessel' && route.isShown) {
      getRoute(targetObject?.mmsi);
    }
  }, [route.isShown, selectedTarget]);

  useEffect(() => {
    if (selectedTarget && selectedTarget.type !== 'vessel') {
      dispatch(setRouteForSelectedVessel({ ...route, isShown: false }));
    }
    if (!selectedTarget) {
      dispatch(setRouteForSelectedVessel({ ...route, isShown: false }));
    }
    if (isHistoryMode) {
      dispatch(setRouteForSelectedVessel({ route: [], currentRoute: [], isShown: false }));
    }
  }, [selectedTarget, isHistoryMode]);

  useEffect(() => {
    if (!isLoading && routeState.isSuccess && routeState.data) {
      const routeCoordinates: number[][] = [];
      const optimizedRoute = getOptimalRoute(routeState.data.results);
      optimizedRoute?.forEach((path: any) => {
        path.map((point: ViewportData) => {
          if (point.lon && !isNaN(point.lon) && point.lat && !isNaN(point.lat)) {
            routeCoordinates.push([point.lon, point.lat]);
          }
        });

        return routeCoordinates;
      });
      if (optimizedRoute && optimizedRoute.length > 0 && route.isShown) {
        dispatch(setRouteForSelectedVessel({ isShown: true, route: optimizedRoute, routePoints: optimizedRoute }));
      }
    }
  }, [isLoading, routeState.data]);

  useEffect(() => {
    if (routeState.isSuccess) {
      const colorizedData = getColorizedRoute(routeState.data.results);
      if (colorizedData && !isHistoryMode) {
        setColorizedRoute(colorizedData);
      } else {
        setColorizedRoute(null);
      }
    }
  }, [routeState.data, isHistoryMode]);

  const routeLayer = new ArcLayer({
    id: 'route-layer',
    visible: !isLoading && route.isShown === true && !isNotBaseMode,
    data: colorizedRoute || [],
    getSourcePosition: (d) => [d.from.lon, d.from.lat],
    getSourceColor: (d) => d.inboundColor,
    getTargetColor: (d) => d.outboundColor,
    getTargetPosition: (d) => [d.to.lon, d.to.lat],
    getHeight: 0,

    positionFormat: 'XY',
    widthMinPixels: 1,
    pickable: false,
    wrapLongitude: true,
  });

  const pointsLayer = new IconLayer({
    id: 'route-points-layer',
    data: colorizedRoute || [],
    pickable: true,
    positionFormat: 'XY',
    getPosition: (d: ColorizedRouteFragment): [number, number, number] => {
      if (typeof d.from?.lon === 'number' && typeof d.from?.lat === 'number') return [d.from.lon, d.from.lat, 0];
      return [0, 0, 10];
    },
    getAngle: (d: ColorizedRouteFragment) => {
      if (typeof d.from?.heading === 'number') return 360 - d.from?.heading;
      if (typeof d.from.cog === 'number') return 360 - d.from?.cog;
      else return 0;
    },
    getIcon: (d: ColorizedRouteFragment) => ({
      url: svgToDataURL(getRouteArrowIcon(d.inboundColor)),
      width: 6,
      height: 8,
    }),
    billboard: true,
    sizeMinPixels: 8,
    autoHighlight: false,
    visible: !isLoading && route.isShown === true && !isNotBaseMode,
    onHover: (point) => {
      dispatch(setHoveredRoutePoint(point));
    },
  });

  return [routeLayer, pointsLayer];
};
